/**
 * Eddie Borgo — Slideshow Touch/Swipe Support
 *
 * Atlantic's slideshow has no native swipe handler. This script adds one
 * by listening to touchstart/touchmove/touchend on the viewport and then
 * calling the existing prev/next buttons.
 *
 * Double-advance defense (multiple layers — earlier single-layer fixes
 * were not enough):
 *
 *  1. ADVANCE_COOLDOWN — at most one scripted advance per gesture.
 *  2. preventDefault on the relevant touchend — suppresses the browser's
 *     synthetic mousedown/mouseup/click chain so that lifting a finger on
 *     top of the .next or .prev button cannot fire a second advance.
 *  3. Capturing-phase click blocker on .next / .prev for CLICK_BLOCK_MS
 *     after a scripted advance — defense-in-depth in case any browser
 *     still emits a synthetic click despite preventDefault.
 *  4. 'paused' class on .slideshow for PAUSE_AFTER_SWIPE so autoplay
 *     can't tack on its own advance immediately after the gesture.
 */
(function () {
  'use strict';

  var SWIPE_THRESHOLD = 50;
  var PAUSE_AFTER_SWIPE = 5000;
  var ADVANCE_COOLDOWN = 600;
  var CLICK_BLOCK_MS = 700;

  var slideshows = document.querySelectorAll('.slideshow');
  if (!slideshows.length) return;

  slideshows.forEach(function (slideshow) {
    var viewport = slideshow.querySelector('.slideshow--viewport');
    if (!viewport) return;

    var prevBtn = slideshow.querySelector('.prev');
    var nextBtn = slideshow.querySelector('.next');
    if (!prevBtn || !nextBtn) return;

    var startX = 0;
    var startY = 0;
    var distX = 0;
    var distY = 0;
    var isSwiping = false;
    var didSwipe = false;
    var resumeTimer = null;
    var lastAdvance = 0;
    var blockClicksUntil = 0;

    function canAdvance() {
      var now = Date.now();
      if (now - lastAdvance < ADVANCE_COOLDOWN) return false;
      lastAdvance = now;
      return true;
    }

    /* Capturing-phase blocker: any click on .next / .prev that lands
       within CLICK_BLOCK_MS of our scripted advance is a stray synthetic
       click from the same gesture. Kill it before Atlantic's jQuery
       handler sees it. */
    function blockStrayClick(e) {
      if (Date.now() < blockClicksUntil) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }
    prevBtn.addEventListener('click', blockStrayClick, true);
    nextBtn.addEventListener('click', blockStrayClick, true);

    viewport.addEventListener('touchstart', function (e) {
      /* Ignore pinch / multi-touch */
      if (e.touches.length !== 1) return;

      var touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      distX = 0;
      distY = 0;
      isSwiping = true;
      didSwipe = false;

      slideshow.classList.add('paused');
      clearTimeout(resumeTimer);
    }, { passive: true });

    viewport.addEventListener('touchmove', function (e) {
      if (!isSwiping) return;
      var touch = e.touches[0];
      distX = touch.clientX - startX;
      distY = touch.clientY - startY;

      /* Horizontal swipe wins → suppress vertical scroll */
      if (Math.abs(distX) > Math.abs(distY) && Math.abs(distX) > 10) {
        e.preventDefault();
      }
    }, { passive: false });

    /* Non-passive touchend so we can preventDefault() and stop the
       synthetic click chain when the finger lifts on top of .next/.prev. */
    viewport.addEventListener('touchend', function (e) {
      if (!isSwiping) return;
      isSwiping = false;

      var horizontalSwipe =
        Math.abs(distX) > SWIPE_THRESHOLD &&
        Math.abs(distX) > Math.abs(distY);

      if (horizontalSwipe && canAdvance()) {
        didSwipe = true;

        /* Suppress the synthetic mousedown/mouseup/click that would
           otherwise fire ~300ms later on whatever element the finger
           lifted from — frequently this is the .next or .prev button,
           which would cause a second advance. */
        if (e.cancelable) e.preventDefault();

        /* distX < 0 → finger moved LEFT → user wants the NEXT slide */
        if (distX < 0) {
          nextBtn.click();
        } else {
          prevBtn.click();
        }

        /* Open the stray-click block window AFTER our scripted click has
           already fired and returned. Any further clicks on .next/.prev
           within CLICK_BLOCK_MS are synthetic clicks from the same
           gesture and must be killed. */
        blockClicksUntil = Date.now() + CLICK_BLOCK_MS;
      }

      /* Keep autoplay paused for a while after the swipe so it doesn't
         tack on an extra advance right after the user just acted. */
      clearTimeout(resumeTimer);
      resumeTimer = setTimeout(function () {
        slideshow.classList.remove('paused');
      }, PAUSE_AFTER_SWIPE);
    }, { passive: false });
  });
})();
